import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { candidateAPI, questionAPI, reportingAPI } from '../../services/api';
import toast from 'react-hot-toast';
import EditCandidateProfile from '../EditCandidateProfile';
import SkillAssessmentsByDifficulty from '../SkillAssessmentsByDifficulty';
import PrescreeningQuestions from '../PrescreeningQuestions';
import ReportList from '../ReportList';
import JobListings from '../JobListings';
import CandidateCourses from '../CandidateCourses';
import OnboardingJourney, { buildJourneySteps } from '../OnboardingJourney';
import ProfileCompletionBar from '../ProfileCompletionBar';
import AiDisclaimer from '../AiDisclaimer';
import { recordJourneyEvent } from '../../utils/journeyEvents';

const CardIcon = ({ d }) => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const JOURNEY_CARD_ICONS = {
  prescreening: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  assessments: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  profile: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  jobs: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0h-8m8 0v2a2 2 0 01-2 2H8a2 2 0 01-2-2V6',
  courses: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
};

const CandidateDashboard = ({ user, candidateView, setCandidateView, onPrescreeningStatusChange, onIsTakingAssessmentChange, pendingAction, onPendingActionHandled }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [showEditProfile, setShowEditProfile] = useState(false);
  const [prescreeningRefreshKey, setPrescreeningRefreshKey] = useState(0);
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [prescreeningStatus, setPrescreeningStatus] = useState({
    hasResume: false,
    isCompleted: false,
    isLoading: true
  });
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [loadingRecommendedJobs, setLoadingRecommendedJobs] = useState(false);
  const [journeyStats, setJourneyStats] = useState({
    assessmentsAvailable: 0,
    assessmentsPassed: 0,
    jobMatchesCount: 0,
    isLoading: true,
  });
  const [isTakingAssessment, setIsTakingAssessment] = useState(false);
  const [hasReport, setHasReport] = useState(false);
  const [hasCompletedAssessment, setHasCompletedAssessment] = useState(false);
  const [inProgressAssessments, setInProgressAssessments] = useState([]);
  const [resumeAssessmentId, setResumeAssessmentId] = useState(null);
  const [exploredLearning, setExploredLearning] = useState(false);
  const [exploredJobs, setExploredJobs] = useState(false);
  const onboardingStartedRef = useRef(false);
  const onboardingCompletedRef = useRef(false);

  useEffect(() => {
    const tab = location.state?.candidateView;
    if (tab === 'courses' || tab === 'dashboard') {
      setCandidateView(tab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, setCandidateView]);

  // Handle isTakingAssessment changes and notify parent
  const handleAssessmentStateChange = (isTaking) => {
    setIsTakingAssessment(isTaking);
    if (onIsTakingAssessmentChange) {
      onIsTakingAssessmentChange(isTaking);
    }
  };

  // Fetch candidate profile and prescreening status
  useEffect(() => {
    const fetchCandidateData = async () => {
      if (user?.userType === 'candidate') {
        try {
          // Fetch profile
          const profileResponse = await candidateAPI.getProfile();
          setCandidateProfile(profileResponse.data);
          const prefs = profileResponse.data?.notificationPreferences || {};
          setExploredLearning(Boolean(prefs.exploredLearning));
          setExploredJobs(Boolean(prefs.exploredJobs));
          if (!prefs.onboardingStartedAt && !onboardingStartedRef.current) {
            onboardingStartedRef.current = true;
            recordJourneyEvent('onboarding_started');
          }
          const hasResume = profileResponse.data?.resumeUrl || profileResponse.data?.resumeExtractedData;
          
          // Check prescreening session status
          let isCompleted = false;
          try {
            const sessionResponse = await candidateAPI.getPrescreeningSession();
            const session = sessionResponse.data;
            isCompleted = session?.sessionStatus === 'completed';
          } catch (error) {
            // No session found, which is fine
            isCompleted = false;
          }
          
          const newStatus = {
            hasResume,
            isCompleted,
            isLoading: false
          };
          setPrescreeningStatus(newStatus);
          if (onPrescreeningStatusChange) {
            onPrescreeningStatusChange(newStatus);
          }
        } catch (error) {
          console.error('Error fetching candidate data:', error);
          const newStatus = {
            hasResume: false,
            isCompleted: false,
            isLoading: false
          };
          setPrescreeningStatus(newStatus);
          if (onPrescreeningStatusChange) {
            onPrescreeningStatusChange(newStatus);
          }
        }
      }
    };
    
    fetchCandidateData();
  }, [user, prescreeningRefreshKey, onPrescreeningStatusChange]);

  useEffect(() => {
    if (user?.userType !== 'candidate') return undefined;
    let cancelled = false;
    const loadJourneyExtras = async () => {
      try {
        if (user?.id) {
          const historyResponse = await questionAPI.getAssessmentHistory(user.id);
          const history = historyResponse.data?.assessments || [];
          if (!cancelled) {
            setHasCompletedAssessment(history.some((item) => item.passed === true || item.status === 'completed'));
          }
        }
      } catch (error) {
        // checklist still works without history
      }
      try {
        if (candidateProfile?.id) {
          const reportsResponse = await reportingAPI.getCandidateReports(candidateProfile.id, null, 5);
          const reports = reportsResponse.data || [];
          if (!cancelled) setHasReport(Array.isArray(reports) && reports.length > 0);
        }
      } catch (error) {
        if (!cancelled) setHasReport(false);
      }
      try {
        const inProgressResponse = await questionAPI.getInProgressAssessments();
        if (!cancelled) setInProgressAssessments(inProgressResponse.data?.attempts || []);
      } catch (error) {
        if (!cancelled) setInProgressAssessments([]);
      }
    };
    loadJourneyExtras();
    return () => {
      cancelled = true;
    };
  }, [user, prescreeningRefreshKey, candidateProfile?.id]);

  useEffect(() => {
    if (candidateView === 'courses') {
      setExploredLearning(true);
      candidateAPI.updatePreferences({ exploredLearning: true }).catch(() => {});
    }
    if (candidateView === 'jobs') {
      setExploredJobs(true);
      candidateAPI.updatePreferences({ exploredJobs: true }).catch(() => {});
    }
  }, [candidateView]);

  useEffect(() => {
    const steps = buildJourneySteps({
      profile: candidateProfile,
      hasResume: prescreeningStatus.hasResume,
      prescreeningCompleted: prescreeningStatus.isCompleted,
      hasCompletedAssessment,
      hasReport,
      hasExploredLearning: exploredLearning,
      hasExploredJobs: exploredJobs,
    });
    if (steps.length && steps.every((step) => step.complete) && candidateProfile) {
      const prefs = candidateProfile.notificationPreferences || {};
      if (!prefs.onboardingCompletedAt && !onboardingCompletedRef.current) {
        onboardingCompletedRef.current = true;
        recordJourneyEvent('onboarding_completed');
      }
    }
  }, [
    candidateProfile,
    prescreeningStatus.hasResume,
    prescreeningStatus.isCompleted,
    hasCompletedAssessment,
    hasReport,
    exploredLearning,
    exploredJobs,
  ]);

  useEffect(() => {
    if (!pendingAction) return;
    if (pendingAction === 'profile') {
      setShowEditProfile(true);
    } else if (pendingAction === 'prescreening') {
      setCandidateView('prescreening');
    } else if (pendingAction === 'continue-assessment') {
      const first = inProgressAssessments[0];
      if (first?.assessment_id && prescreeningStatus.isCompleted) {
        setResumeAssessmentId(first.assessment_id);
        setCandidateView('assessments');
      } else {
        setCandidateView('prescreening');
      }
    }
    if (onPendingActionHandled) onPendingActionHandled();
  }, [pendingAction, inProgressAssessments, prescreeningStatus.isCompleted, onPendingActionHandled, setCandidateView]);

  useEffect(() => {
    const fetchDashboardSideData = async () => {
      if (user?.userType === 'candidate' && user?.id && candidateView === 'dashboard') {
        try {
          const historyResponse = await questionAPI.getAssessmentHistory(user.id);
          const allAssessments = historyResponse.data?.assessments || [];
          const assessmentsResponse = await questionAPI.getAssessmentsByDifficulty(user.id);
          const assessments = assessmentsResponse.data?.assessments_by_difficulty || {};
          const allAvailable = [
            ...(assessments.beginner || []),
            ...(assessments.intermediate || []),
            ...(assessments.advanced || [])
          ];
          setJourneyStats((prev) => ({
            ...prev,
            assessmentsAvailable: allAvailable.length,
            assessmentsPassed: allAssessments.filter((item) => item.passed === true).length,
            isLoading: false,
          }));
        } catch (error) {
          console.error('Error fetching assessment stats:', error);
          setJourneyStats((prev) => ({ ...prev, isLoading: false }));
        }

        setLoadingRecommendedJobs(true);
        try {
          const jobsResponse = await candidateAPI.getJobRecommendations(1, 4);
          const jobs = jobsResponse.data?.jobs || [];
          setRecommendedJobs(jobs);
          setJourneyStats((prev) => ({
            ...prev,
            jobMatchesCount: jobsResponse.data?.total || jobs.length,
            isLoading: false,
          }));
        } catch (error) {
          console.error('Error fetching recommended jobs:', error);
          setRecommendedJobs([]);
          setJourneyStats((prev) => ({ ...prev, jobMatchesCount: 0, isLoading: false }));
        } finally {
          setLoadingRecommendedJobs(false);
        }
      }
    };

    fetchDashboardSideData();
  }, [user, candidateView, prescreeningRefreshKey]);

  // Redirect to dashboard if on assessments view but prescreening not completed
  useEffect(() => {
    if (user?.userType === 'candidate' && candidateView === 'assessments' && 
        !prescreeningStatus.isLoading && !prescreeningStatus.isCompleted) {
      setCandidateView('dashboard');
      toast.error(
        !prescreeningStatus.hasResume
          ? 'Please upload your resume and complete prescreening assessment first'
          : 'Please complete your prescreening assessment first'
      );
    }
  }, [candidateView, prescreeningStatus, user]);

  const handleJourneyStepClick = (step, meta = {}) => {
    const view = step?.ctaView;
    recordJourneyEvent('checklist_item_clicked', {
      step: step?.id,
      source: meta.source || 'checklist',
      complete: Boolean(step?.complete),
    });
    if (view === 'profile') setShowEditProfile(true);
    else if (view === 'prescreening') setCandidateView('prescreening');
    else if (view === 'continue-assessment') {
      const first = inProgressAssessments[0];
      if (first?.assessment_id) {
        setResumeAssessmentId(first.assessment_id);
        setCandidateView('assessments');
      }
    } else if (view) setCandidateView(view);
  };

  const journeyCards = [
    {
      id: 'prescreening',
      title: 'Prescreening',
      value: prescreeningStatus.isLoading ? '...' : (prescreeningStatus.isCompleted ? 1 : 0),
      meta: 'of 1 complete',
      body: !prescreeningStatus.hasResume
        ? 'Upload your resume, then complete a short background questionnaire.'
        : prescreeningStatus.isCompleted
          ? 'Prescreening is complete. You can review your submission anytime.'
          : 'Answer a short set of questions before skill assessments unlock.',
      cta: prescreeningStatus.isCompleted ? 'View Prescreening →' : 'Start Prescreening →',
      ctaView: 'prescreening',
      complete: prescreeningStatus.isCompleted,
      ariaLabel: prescreeningStatus.isLoading
        ? 'Prescreening, loading'
        : `Prescreening, ${prescreeningStatus.isCompleted ? 1 : 0} of 1 complete`,
      icon: <CardIcon d={JOURNEY_CARD_ICONS.prescreening} />,
    },
    {
      id: 'assessment',
      title: 'Know Your Strengths',
      value: journeyStats.isLoading ? '...' : journeyStats.assessmentsAvailable,
      meta: 'assessments available',
      body: 'Take a short assessment to understand your current skill level and identify areas where you can improve.',
      cta: 'View pending assessments →',
      ctaView: prescreeningStatus.isCompleted ? 'assessments' : 'prescreening',
      complete: hasCompletedAssessment,
      ariaLabel: journeyStats.isLoading
        ? 'Know Your Strengths, loading'
        : `Know Your Strengths, ${journeyStats.assessmentsAvailable} assessments available`,
      icon: <CardIcon d={JOURNEY_CARD_ICONS.assessments} />,
    },
    {
      id: 'profile',
      title: 'Build Your Career',
      value: `${candidateProfile?.profileCompletionPercentage || 0}%`,
      meta: 'profile complete',
      body: 'Add your skills, education and experience so Liaizn can give you more relevant recommendations.',
      cta: 'Complete Profile →',
      ctaView: 'profile',
      complete: (candidateProfile?.profileCompletionPercentage || 0) >= 100,
      ariaLabel: `Build Your Career, ${candidateProfile?.profileCompletionPercentage || 0} percent complete`,
      icon: <CardIcon d={JOURNEY_CARD_ICONS.profile} />,
    },
    {
      id: 'jobs',
      title: 'Discover Opportunities',
      value: journeyStats.isLoading ? '...' : journeyStats.jobMatchesCount,
      meta: 'matching jobs',
      body: 'Explore career opportunities that match your skills and interests.',
      cta: 'Explore Jobs →',
      ctaView: 'jobs',
      complete: exploredJobs,
      ariaLabel: journeyStats.isLoading
        ? 'Discover Opportunities, loading'
        : `Discover Opportunities, ${journeyStats.jobMatchesCount} matching jobs`,
      icon: <CardIcon d={JOURNEY_CARD_ICONS.jobs} />,
    },
    {
      id: 'learning',
      title: 'Learn What You Are Missing',
      value: journeyStats.isLoading ? '...' : journeyStats.assessmentsPassed,
      meta: 'assessments passed',
      body: 'Get learning recommendations based on the skills you need to improve.',
      cta: 'Explore Learning →',
      ctaView: 'courses',
      complete: exploredLearning,
      ariaLabel: journeyStats.isLoading
        ? 'Learn What You Are Missing, loading'
        : `Learn What You Are Missing, ${journeyStats.assessmentsPassed} assessments passed`,
      icon: <CardIcon d={JOURNEY_CARD_ICONS.courses} />,
    },
  ];

  return (
    <>
      {candidateView === 'dashboard' ? (
        <div className="flex flex-col gap-3 pb-6">
          <ProfileCompletionBar
            compact
            percentage={candidateProfile?.profileCompletionPercentage || 0}
            nextHint={candidateProfile?.profileCompletionNextHint}
            onCompleteClick={() => setShowEditProfile(true)}
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {journeyCards.map((card) => (
              <button
                key={card.id}
                type="button"
                onClick={() => handleJourneyStepClick(
                  { id: card.id, ctaView: card.ctaView, complete: card.complete },
                  { source: 'journey-card' },
                )}
                className="kpi-card group"
                aria-label={card.ariaLabel}
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600" aria-hidden="true">
                    {card.icon}
                  </span>
                  <h3 className="text-sm font-semibold leading-snug text-secondary-900">{card.title}</h3>
                </div>
                <p className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-2xl font-semibold tracking-tight text-secondary-900">{card.value}</span>
                  <span className="text-xs font-medium text-secondary-500">{card.meta}</span>
                </p>
                <p className="mt-2 hidden line-clamp-2 text-xs leading-5 text-secondary-600 sm:block">{card.body}</p>
                <span className="mt-auto inline-flex items-center gap-1 pt-3 text-sm font-semibold text-primary-700 group-hover:text-primary-800">
                  {String(card.cta).replace(/\s*→\s*$/, '')}
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]">
            <div className="min-w-0 h-full">
              <OnboardingJourney
                steps={buildJourneySteps({
                  profile: candidateProfile,
                  hasResume: prescreeningStatus.hasResume,
                  prescreeningCompleted: prescreeningStatus.isCompleted,
                  hasCompletedAssessment,
                  hasReport,
                  hasExploredLearning: exploredLearning,
                  hasExploredJobs: exploredJobs,
                })}
                inProgressAssessment={prescreeningStatus.isCompleted ? inProgressAssessments[0] : null}
                onStepClick={handleJourneyStepClick}
              />
            </div>

          <div className="min-w-0 h-full">
            <div className="ui-card flex h-full min-h-0 flex-col p-4">
              <div className="flex shrink-0 items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-secondary-900">Recommended Jobs</h2>
                <button type="button" onClick={() => setCandidateView('jobs')} className="text-xs font-semibold text-primary-700 hover:text-primary-800">
                  View all
                </button>
              </div>
              {loadingRecommendedJobs ? (
                <div className="flex flex-1 items-center justify-center">
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" />
                </div>
              ) : recommendedJobs.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center px-2 text-center">
                  <p className="text-sm font-medium text-secondary-800">No matches yet</p>
                  <p className="mt-1 text-xs text-secondary-500">Add skills to your profile to get job recommendations.</p>
                  <button type="button" onClick={() => setCandidateView('jobs')} className="btn-ghost mt-3">
                    Browse jobs
                  </button>
                </div>
              ) : (
                <div className="mt-3 flex min-h-0 flex-1 flex-col gap-2">
                  {recommendedJobs.slice(0, 4).map((matchedJob, index) => {
                    const job = matchedJob.job || matchedJob;
                    const matchedSkills = matchedJob.matchedSkills || matchedJob.matched_skills || [];
                    const locationLabel = [job.companyName, job.location].filter(Boolean).join(' · ');
                    return (
                      <button
                        key={job.id || index}
                        type="button"
                        className="w-full rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-left hover:border-secondary-300 hover:bg-secondary-100"
                        onClick={() => setCandidateView('jobs')}
                      >
                        <p className="truncate text-sm font-semibold text-secondary-900">{job.title}</p>
                        {locationLabel ? (
                          <p className="mt-0.5 truncate text-xs text-secondary-500">{locationLabel}</p>
                        ) : null}
                        {matchedSkills.length > 0 ? (
                          <p className="mt-1.5 truncate text-xs text-secondary-600">
                            {matchedSkills.slice(0, 3).join(' · ')}
                            {matchedSkills.length > 3 ? ` +${matchedSkills.length - 3}` : ''}
                          </p>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      ) : (
      <div className="space-y-6">
        {candidateView === 'assessments' ? (
          prescreeningStatus.isCompleted ? (
            <SkillAssessmentsByDifficulty
              onAssessmentStateChange={handleAssessmentStateChange}
              resumeAssessmentId={resumeAssessmentId}
              onResumeHandled={() => setResumeAssessmentId(null)}
            />
          ) : (
            <div className="empty-state ui-card">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">Assessments Locked</h3>
              <p className="text-secondary-600 mb-4 max-w-md mx-auto">
                {!prescreeningStatus.hasResume
                  ? 'Please upload your resume and complete prescreening assessment to unlock skill assessments'
                  : 'Please complete your prescreening assessment to unlock skill assessments'}
              </p>
              <button
                onClick={() => setCandidateView('prescreening')}
                className="btn-primary"
              >
                Start Prescreening
              </button>
            </div>
          )
        ) : candidateView === 'reports' ? (
          <ReportList
            candidateId={candidateProfile?.id}
            showGenerateButton={false}
            showHeader={false}
          />
        ) : candidateView === 'jobs' ? (
          <div className="space-y-4">
            <AiDisclaimer variant="jobs" />
            <JobListings />
          </div>
        ) : candidateView === 'courses' ? (
          <CandidateCourses />
        ) : candidateView === 'prescreening' ? (
          <div>
            <div className="mb-6">
              <button
                onClick={() => setCandidateView('dashboard')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Dashboard
              </button>
            </div>
            <PrescreeningQuestions 
              onComplete={() => {
                setPrescreeningRefreshKey(prev => prev + 1);
                setCandidateView('dashboard');
              }}
            />
          </div>
        ) : null}
      </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfile && (
        <EditCandidateProfile
          onClose={() => {
            setShowEditProfile(false);
            setPrescreeningRefreshKey(prev => prev + 1);
          }}
          onSuccess={() => {
            console.log('Profile updated successfully');
          }}
        />
      )}
    </>
  );
};

export default CandidateDashboard;

